import { useNavigate } from 'react-router-dom';
import { OnboardingState } from '../OnboardingWizard';
import {
  CheckCircle,
  Building2,
  Users,
  ShieldCheck,
  ArrowRight,
  FileText,
  ClipboardList,
} from 'lucide-react';

interface Props {
  state: OnboardingState;
}

export default function CompleteStep({ state }: Props) {
  const navigate = useNavigate();

  const completedItems = [
    {
      icon: Building2,
      title: 'Client Information',
      description: `${state.companyName} has been added`,
      complete: true,
    },
    {
      icon: ShieldCheck,
      title: 'GDAP Relationship',
      description: 'Delegated admin access established',
      complete: !!state.relationshipId,
    },
    {
      icon: Users,
      title: 'AdminAgents Assigned',
      description: 'Security group linked to GDAP',
      complete: state.adminAgentsAssigned,
    },
    {
      icon: FileText,
      title: 'Application Consent',
      description: 'Reports app has tenant access',
      complete: state.appConsentGranted,
    },
  ];

  const nextSteps = [
    {
      icon: Users,
      title: 'Manage Users',
      description: 'View and manage Microsoft 365 users in the customer tenant',
      action: () => navigate(`/clients/${state.clientId}/users`),
      buttonText: 'Go to Users',
      disabled: !state.clientId,
    },
    {
      icon: ClipboardList,
      title: 'View Subscriptions',
      description: 'Sync and manage Pax8 and Microsoft subscriptions',
      action: () => navigate('/subscriptions'),
      buttonText: 'View Subscriptions',
      disabled: false,
    },
    {
      icon: FileText,
      title: 'Generate Reports',
      description: 'Create MFA, license, and security reports',
      action: () => navigate('/scheduled-reports'),
      buttonText: 'Scheduled Reports',
      disabled: false,
    },
  ];

  return (
    <div>
      <div className="text-center mb-8">
        <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4">
          <CheckCircle className="h-10 w-10 text-green-600" />
        </div>
        <h2 className="text-2xl font-bold text-foreground mb-2">
          Onboarding Complete!
        </h2>
        <p className="text-muted-foreground">
          {state.companyName} has been successfully onboarded
        </p>
      </div>

      {/* Summary */}
      <div className="bg-muted rounded-lg p-6 mb-8">
        <h3 className="text-lg font-semibold text-foreground mb-4">
          Setup Summary
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {completedItems.map((item, index) => (
            <div
              key={index}
              className="flex items-start p-3 bg-card rounded-lg"
            >
              <div className={`p-2 rounded-lg mr-3 ${item.complete ? 'bg-green-100 dark:bg-green-900/30' : 'bg-muted'}`}>
                <item.icon className={`h-5 w-5 ${item.complete ? 'text-green-600' : 'text-gray-400'}`} />
              </div>
              <div>
                <h4 className="font-medium text-foreground text-sm">
                  {item.title}
                </h4>
                <p className="text-xs text-muted-foreground">
                  {item.description}
                </p>
              </div>
              {item.complete && (
                <CheckCircle className="h-5 w-5 text-green-500 ml-auto" />
              )}
            </div>
          ))}
        </div>

        {/* Tenant Details */}
        {state.customerTenantId && (
          <div className="mt-4 pt-4 border-t border-border">
            <h4 className="text-sm font-medium text-muted-foreground mb-2">
              Customer Tenant Details
            </h4>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div>
                <dt className="text-muted-foreground">Display Name</dt>
                <dd className="font-medium text-foreground">
                  {state.customerDisplayName || state.companyName}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Tenant ID</dt>
                <dd className="font-mono text-xs text-foreground">
                  {state.customerTenantId}
                </dd>
              </div>
              {state.relationshipId && (
                <div className="sm:col-span-2">
                  <dt className="text-muted-foreground">GDAP Relationship ID</dt>
                  <dd className="font-mono text-xs text-foreground break-all">
                    {state.relationshipId}
                  </dd>
                </div>
              )}
            </dl>
          </div>
        )}
      </div>

      {/* Next Steps */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-foreground mb-4">
          What's Next?
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {nextSteps.map((step, index) => (
            <div
              key={index}
              className="border border-border rounded-lg p-4 hover:border-blue-500 dark:hover:border-blue-400 transition-colors"
            >
              <div className="flex items-start mb-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg mr-3">
                  <step.icon className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-medium text-foreground text-sm">
                    {step.title}
                  </h4>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                {step.description}
              </p>
              <button
                onClick={step.action}
                disabled={step.disabled}
                className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {step.buttonText}
                <ArrowRight className="h-4 w-4 ml-1" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-4 pt-4 border-t border-border">
        <button
          onClick={() => navigate('/clients')}
          className="btn-primary flex-1 sm:flex-none"
        >
          <Building2 className="h-4 w-4 mr-2" />
          View All Clients
        </button>
        <button
          onClick={() => window.location.reload()}
          className="btn-secondary flex-1 sm:flex-none"
        >
          Onboard Another Client
        </button>
      </div>
    </div>
  );
}
